// src/components/trades/analysis/StandingsProjectionSection.js
import React from 'react';
import { dynastyTheme } from '../../../services/colorService';
import { Trophy, TrendingUp, TrendingDown, Minus } from 'lucide-react';

export const StandingsProjectionSection = ({ data }) => {
  if (!data) return null;

  const { proposing_team, receiving_team, standings } = data;

  const renderTeamProjection = (teamData, teamName) => {
    if (!teamData) return null;
    const pointChange = teamData.point_change || 0;
    const categoryChanges = teamData.category_changes || {};
    const changedCats = Object.entries(categoryChanges).filter(([, v]) => v.pts_delta !== 0);

    return (
      <div className="flex-1">
        <h4 className="text-lg font-semibold text-white mb-3">{teamName}</h4>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-4">
            <div className="p-3 bg-gray-800/50 rounded">
              <div className="text-xs text-gray-400 mb-1">Current Rank</div>
              <div className="text-2xl font-bold text-white">#{teamData.current_rank || '—'}</div>
              <div className="text-xs text-gray-400">{teamData.current_points != null ? `${teamData.current_points.toFixed(1)} pts` : '--'}</div>
            </div>
            <div className="p-3 bg-gray-800/50 rounded">
              <div className="text-xs text-gray-400 mb-1">Projected Points</div>
              <div className="text-2xl font-bold text-white">{teamData.projected_points != null ? teamData.projected_points.toFixed(1) : '—'}</div>
              <div className={`text-xs font-bold ${pointChange >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                {pointChange >= 0 ? '+' : ''}{pointChange.toFixed(1)} pts
              </div>
            </div>
          </div>

          <div className={`p-3 rounded border ${dynastyTheme.classes.border.neutral} bg-gray-800/30`}>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-400">Points Impact:</span>
              <div className={`flex items-center gap-1 font-bold ${pointChange > 0 ? 'text-emerald-400' : pointChange < 0 ? 'text-red-400' : 'text-yellow-400'}`}>
                {pointChange > 0 ? <TrendingUp className="w-4 h-4" /> : pointChange < 0 ? <TrendingDown className="w-4 h-4" /> : <Minus className="w-4 h-4" />}
                <span>{pointChange >= 0 ? '+' : ''}{pointChange.toFixed(1)} pts</span>
              </div>
            </div>
          </div>

          {/* Per-category rank changes */}
          {changedCats.length > 0 && (
            <div className="space-y-1">
              <div className="text-xs text-gray-500 font-semibold uppercase tracking-wide">Category Impact</div>
              {changedCats.sort((a, b) => Math.abs(b[1].pts_delta) - Math.abs(a[1].pts_delta)).map(([cat, change]) => (
                <div key={cat} className="flex items-center justify-between text-xs py-0.5">
                  <span className="text-gray-400 w-8">{cat}</span>
                  {change.rank_before != null && change.rank_after != null && (
                    <span className="text-gray-500">
                      {change.rank_before}{change.rank_before !== change.rank_after ? ` → ${change.rank_after}` : ''}
                    </span>
                  )}
                  <span className={`font-mono font-bold ${change.pts_delta > 0 ? 'text-emerald-400' : change.pts_delta < 0 ? 'text-red-400' : 'text-gray-500'}`}>
                    {change.pts_delta > 0 ? '+' : ''}{change.pts_delta}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className={`p-4 ${dynastyTheme.classes.bg.darkCard} rounded-lg border ${dynastyTheme.classes.border.neutral}`}>
      <h3 className={`text-lg font-bold ${dynastyTheme.classes.text.primary} mb-4 flex items-center gap-2`}>
        <Trophy className="w-5 h-5" />
        Standings Projection
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {renderTeamProjection(proposing_team, "Your Team")}
        {renderTeamProjection(receiving_team, "Their Team")}
      </div>
      <div className="mt-4 text-xs text-gray-500 italic">
        * Based on current season rotisserie standings and player stats
      </div>
    </div>
  );
};

export default StandingsProjectionSection;
